import {
    Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BridgePortSelectorComponent, ToolsService } from 'tools';
import { MessageService } from 'base';
import { Device } from 'DiscoveryEngine.TS/Core';
import { List, ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IdentityService, UserPermissionsService } from 'identity';
import {
    DisplayDeviceListEntryDto, GetDeviceResultDto, IGetPortStateResultDto, ISetPortStateResultDto, PortStateDto,
    IPortStateDto_Get
} from 'UvExplorerServer.Dtos.TS/Session';
import { IfEntry, IfTable } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { PortStateType } from 'UvExplorerServer.Dtos.TS/Session';
import { BridgePortEntry, BridgePortTable } from 'DiscoveryEngine.TS/InfoSets/BridgePortTable';
import { VlanEntry } from 'DiscoveryEngine.TS/InfoSets/VlanTable';
import { NavigationService } from 'netview';
import { DiscResultsService } from '../../../discovery';

@Component({
    selector: 'port-access-vlan-editor',
    templateUrl: './port-access-vlan-editor.component.html',
    styleUrls: ['./port-access-vlan-editor.component.scss']
})
export class PortAccessVlanEditorComponent implements OnChanges, OnDestroy, OnInit {

    @Input() public deviceGuid: string;

    @Input()
    public portTableHeightVh: number;

    @Input()
    public portTableHeightPx: number;

    @Input()
    public portTableMinHeight: number;

    @Input()
    public visible: boolean;

    @ViewChild(BridgePortSelectorComponent, { static: true })
    public portSelector: BridgePortSelectorComponent;

    public device: Device;
    public deviceName: string;
    public displayDeviceSelector: boolean;
    public vlans: VlanEntry[];
    public voiceVlans: VlanEntry[];

    public form: UntypedFormGroup;
    public vlan: UntypedFormControl;
    public voiceVlan: UntypedFormControl;

    public selectedPorts: BridgePortEntry[];

    constructor(private formBuilder: UntypedFormBuilder,
        private navService: NavigationService,
        private messageService: MessageService,
        private toolsService: ToolsService,
        private discResultsService: DiscResultsService,
        private identityService: IdentityService,
        private userPermissionsService: UserPermissionsService,
        private changeDetector: ChangeDetectorRef
    ) {
        this.setDevice(null);
        this.displayDeviceSelector = false;
        this.form = null;
        this.vlan = null;
        this.voiceVlan = null;
        this.clearSelectedInterfaces();
    }

    public ngOnChanges(changes: SimpleChanges): void {

        if (!!changes["deviceGuid"] || !!changes["visible"]) {
            if (!!this.deviceGuid && this.visible) {
                setTimeout(() => this.getDevice(), 0);
            }
            else {
                if (this.vlan) {
                    this.vlan.setValue("");
                }
                if (this.voiceVlan) {
                    this.voiceVlan.setValue("");
                }
                this.setDevice(null);
            }
        }
    }

    public ngOnInit(): void {
        this.rebuildForm();
    }

    public ngOnDestroy(): void {

    }

    private clearSelectedInterfaces(): void {
        this.selectedPorts = [];
    }

    private rebuildForm(): void {
        let vlan = -1;
        let voiceVlanIndex = -1;

        this.vlan = new UntypedFormControl(vlan);
        this.voiceVlan = new UntypedFormControl(voiceVlanIndex);

        this.form = this.formBuilder.group({
            vlan: vlan,
            voiceVlan: voiceVlanIndex,
        });
    }

    private initVlans(): void {
        this.vlans = [];
        this.voiceVlans = [];

        if (this.vlan) {
            this.vlan.setValue(-1);
        }
        if (this.voiceVlan) {
            this.voiceVlan.setValue(-1);
        }

        if (!!this.device && this.device.HasVlanTable) {
            let defaultVlan = new VlanEntry(0);
            defaultVlan.Name = 'NONE';
            this.voiceVlans.push(defaultVlan);

            this.device.VlanTable.ForEach((vlanEntry: VlanEntry) => {
                this.vlans.push(vlanEntry);
                this.voiceVlans.push(vlanEntry);
            });

            if (this.vlans.length > 0) {
                this.vlans.sort((a: VlanEntry, b: VlanEntry) => {
                    if (a.Index < b.Index) {
                        return -1;
                    }
                    else if (a.Index > b.Index) {
                        return 1;
                    }
                    else {
                        return 0;
                    }
                });

                if (this.vlan) {
                    this.vlan.setValue(this.vlans[0].Index);
                }
            }

            if (this.voiceVlans.length > 0) {
                this.voiceVlans.sort((a: VlanEntry, b: VlanEntry) => {
                    if (a.Index < b.Index) {
                        return -1;
                    }
                    else if (a.Index > b.Index) {
                        return 1;
                    }
                    else {
                        return 0;
                    }
                });

                if (this.voiceVlan) {
                    this.voiceVlan.setValue(this.voiceVlans[0].Index);
                }
            }

        }
    }

    public get enableSelectDevice(): boolean {
        return true;
    }

    public get enableUpdate(): boolean {
        return (this.userPermissionsService.canManagePortAccessVlans &&
            !!this.device && (this.vlan.value >= 0) && !!this.selectedPorts &&
            (this.selectedPorts.length > 0));
    }

    public get enableRefresh(): boolean {
        return !!this.device;
    }

    public selectDevice(): void {
        this.displayDeviceSelector = true;
    }

    public update(): void {
        if (!!this.device && !!this.vlan.value && !!this.selectedPorts && (this.selectedPorts.length > 0)) {

            let sessionGuid = this.identityService.sessionGuid;
            let netViewGuid = this.navService.location.netViewGuid;
            let agentGuid = this.navService.location.agentGuid;
            let schedDiscGuid = this.navService.location.schedDiscGuid;
            let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

            let portStateDtos: PortStateDto[] =
                this.selectedPorts.map((bpEntry) => this.createPortStateDto(this.device, bpEntry));

            this.messageService.startWork();
            this.toolsService.setPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
                schedDiscRsltGuid, PortStateType.AccessVlan, portStateDtos, true).subscribe(
                    (result: ISetPortStateResultDto) => {
                        this.messageService.endWork();
                        if (this.device) {
                            let succeeded = false;
                            if (result.entries) {
                                succeeded = true;
                                result.entries.forEach((entry: IPortStateDto_Get) => {
                                    if (entry.succeeded) {
                                        let bpEntry: BridgePortEntry = this.device.BridgePortTable.Find(entry.bridgePortIndex);
                                        let ifEntry: IfEntry = this.device.IfTable.Find(entry.ifIndex);
                                        let vlanEntry: VlanEntry = this.device.VlanTable.Find(entry.vlanIndex);
                                        if (!!bpEntry && !!ifEntry && !!vlanEntry) {
                                            bpEntry.VlanIndex = vlanEntry.Index;
                                            this.portSelector.rebuildEntries();
                                        }
                                        else {
                                            if (!bpEntry) {
                                                console.error(`Missing bridge port: ${this.device.DisplayName} - ${entry.bridgePortIndex}`);
                                            }
                                            if (!ifEntry) {
                                                console.error(`Missing interface: ${this.device.DisplayName} - ${entry.ifIndex}`);
                                            }
                                            if (!vlanEntry) {
                                                console.error(`Missing VLAN: ${this.device.DisplayName} - ${entry.vlanIndex}`);
                                            }
                                        }

                                    }
                                    else {
                                        succeeded = false;
                                    }
                                });
                            }
                            if (succeeded) {
                                this.messageService.displayInfoMessage("Ports were successfully configured");
                            }
                            else {
                                this.messageService.displayErrorMessage("Some or all ports could not be configured");
                            }

                            //this.getDevicePortState();
                            this.getDevice();
                        }
                    },
                    (error) => {
                        this.messageService.endWork();
                        this.messageService.displayErrorMessage("Some or all ports could not be configured");
                        //this.getDevicePortState();
                        this.getDevice();
                    });
        }
    }

    private createPortStateDto(device: Device, bpEntry: BridgePortEntry): PortStateDto {
        let result = new PortStateDto();

        result.DeviceGuid = ValueEncoder.EncodeGuid(device.GUID);
        result.IfIndex = bpEntry.IfIndex;
        result.BridgePortIndex = bpEntry.Index;
        result.VlanIndex = this.vlan.value;
        result.VoiceVlanIndex = -1;
        if (this.voiceVlan.value >= 1)
            result.VoiceVlanIndex = this.voiceVlan.value;

        return result;
    }

    public refresh(): void {
        this.getDevicePortState();
    }

    private setDevice(device: Device): void {
        if (device) {
            this.device = device;
            this.deviceName = device.DisplayName;
        }
        else {
            this.device = null;
            this.deviceName = "No device is selected";
        }
    }

    public onDeviceSelectorOk(devices: DisplayDeviceListEntryDto[]): void {
        this.deviceGuid = devices[0].deviceGuid;
        this.getDevice();
    }

    public onDeviceSelectorCancel(): void {
        this.displayDeviceSelector = false;
    }

    private getDevice(): void {

        let sessionGuid = this.identityService.sessionGuid;
        let netViewGuid = this.navService.location.netViewGuid;
        let agentGuid = this.navService.location.agentGuid;
        let schedDiscGuid = this.navService.location.schedDiscGuid;
        let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

        let deviceGuids = new List<string>();
        deviceGuids.Add(this.deviceGuid);

        this.messageService.startWork();
        this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
            schedDiscRsltGuid, "network", deviceGuids, true).subscribe(
                (result: GetDeviceResultDto) => {
                    this.messageService.endWork();
                    if (result.HasDevices) {
                        this.setDevice(result.Devices.Get(0));
                        this.initVlans();
                        //this.getDevicePortState();
                        this.changeDetector.detectChanges();
                    }
                    else {
                        this.messageService.displayErrorMessage("Could not retrieve selected device");
                    }
                },
                (error?) => {
                    this.messageService.endWork();
                    this.messageService.displayErrorMessage("Could not retrieve selected device");
                });
    }

    private getDevicePortState(): void {

        if (!!this.device && this.device.HasIfTable) {
            let sessionGuid = this.identityService.sessionGuid;
            let netViewGuid = this.navService.location.netViewGuid;
            let agentGuid = this.navService.location.agentGuid;
            let schedDiscGuid = this.navService.location.schedDiscGuid;
            let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

            this.messageService.startWork();
            this.toolsService.getPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
                schedDiscRsltGuid, [PortStateType.AccessVlan, PortStateType.AdminStatus, PortStateType.ConnectedDevices],
                [this.deviceGuid], [], true).subscribe(
                    (result: IGetPortStateResultDto) => {
                        this.messageService.endWork();
                        if (!!this.device && this.device.HasIfTable) {
                            let errors = false;
                            let message = "";

                            if (!result.entries || result.entries.length == 0) {
                                errors = true;
                            }
                            else {
                                errors = result.entries.some((entry: IPortStateDto_Get) => {
                                    if (!entry.succeeded) {
                                        message = entry.message;
                                        return true;
                                    }
                                    else {
                                        return false;
                                    }
                                });
                            }

                            if (errors) {
                                this.messageService.displayErrorMessage("Could not retrieve device port state");
                                console.error(message);
                            }
                            else {
                                let ifTable: IfTable = this.device.IfTable;
                                let bpTable: BridgePortTable = this.device.BridgePortTable;

                                result.entries.forEach((entry: IPortStateDto_Get) => {
                                    let ifEntry: IfEntry = ifTable.Find(entry.ifIndex);
                                    if (ifEntry) {
                                        ifEntry.Description = entry.description;
                                        ifEntry.AdminStatus = entry.adminStatus;
                                        ifEntry.OperStatus = entry.operStatus;
                                        ifEntry.ConnectedDevices = entry.connectedDevices;

                                        if (bpTable) {
                                            let bpEntry: BridgePortEntry = bpTable.FindByIfIndex(entry.ifIndex);
                                            if (bpEntry) {
                                                bpEntry.VlanIndex = entry.vlanIndex;
                                            }
                                        }
                                    }
                                    else {
                                        console.warn(`Missing device interface: ${this.device.DisplayName} - ${entry.ifIndex}`);
                                    }
                                });

                                this.portSelector.rebuildEntries();
                                this.changeDetector.detectChanges();
                            }
                        }
                        this.getDevice();
                    },
                    (error?) => {
                        this.messageService.endWork();
                        this.messageService.displayErrorMessage("Could not retrieve device port state");
                    });
        }
    }

}
